/**
 * Per-participant prize display for TEAM entries.
 *
 * A team roping entry is a single entry that wins a single prize, so the
 * results boards correctly show the TEAM total. Contestants also want to know
 * what each half of the team takes home, so the amount renders as:
 *
 *     $500.00 ($250.00 each)
 *
 * The split is derived from the entry's own participant names rather than a
 * hardcoded `/2`: a solo entry (one named contestant) gets no parenthetical,
 * and any future multi-person event splits by its real head count.
 *
 * Rounding: the share is computed in whole cents and floored, so the shares
 * shown can never add up to MORE than the team total (a $100.01 prize shows
 * "$50.00 each", not "$50.01 each"). The parenthetical is informational — the
 * payout of record stays the team total.
 */

import { formatCurrency } from './format';

/**
 * Placeholder the standings serializer emits for the missing half of a solo
 * entry (`pickMemberNames` returns `[own, '—']` for non-team events).
 */
const NAME_PLACEHOLDER = '—';

/**
 * How many people share this entry's prize, from its standings member names.
 * Placeholder/blank names don't count, so a solo entry resolves to 1 and a
 * team roping entry to 2. Never returns less than 1.
 */
export function countPrizeParticipants(
  memberNames: (string | null | undefined)[] | null | undefined,
): number {
  if (!Array.isArray(memberNames)) return 1;
  const named = memberNames.filter(
    (n) => typeof n === 'string' && n.trim() !== '' && n.trim() !== NAME_PLACEHOLDER,
  );
  return Math.max(1, named.length);
}

/**
 * Each participant's share of `amount`, floored to the cent. Null whenever
 * there is nothing meaningful to split — no/zero/negative prize, or a
 * single-participant entry (whose share IS the total).
 */
export function prizeShareEach(
  amount: number | null | undefined,
  participantCount: number,
): number | null {
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) return null;
  if (!Number.isFinite(participantCount) || participantCount < 2) return null;
  const shareCents = Math.floor(Math.round(amount * 100) / Math.trunc(participantCount));
  if (shareCents <= 0) return null;
  return shareCents / 100;
}

/** `"$250.00 each"`, or null when there is no split to show. */
export function formatPrizeShareEach(
  amount: number | null | undefined,
  participantCount: number,
): string | null {
  const share = prizeShareEach(amount, participantCount);
  return share == null ? null : `${formatCurrency(share)} each`;
}

/**
 * The full prize cell: `"$500.00 ($250.00 each)"` for a team entry, plain
 * `"$500.00"` for a solo one, and `''` when no prize is payable (or the
 * amount is being withheld until results are published).
 */
export function formatPrizeWithShare(
  amount: number | null | undefined,
  participantCount: number,
): string {
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) return '';
  const each = formatPrizeShareEach(amount, participantCount);
  return each ? `${formatCurrency(amount)} (${each})` : formatCurrency(amount);
}
